import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LoadingButton from "@mui/lab/LoadingButton";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import ruLocale from "date-fns/locale/ru";
import { js2xml } from "xml-js";
import DashboardLayout from "../../layouts/Dashboard";
import {
  ContractSelect,
  FilialDivisionSelect,
  FilialSelect,
  PlanDistrictCitySelect,
} from "../../components/Select/domainSelects";
import useAlert from "../../hooks/useAlert";
import useLoader from "../../hooks/useLoader";
import {
  planCRUD,
  planDocumentCRUD,
  planIndexCRUD,
  planServiceCRUD,
  serviceCRUD,
} from "../../http";
import {
  PLAN_DETAIL_ROUTE,
  PLAN_DOCUMENT_ROUTE,
  PLAN_EDIT_ROUTE,
  PLAN_LIST_ROUTE,
} from "../../routes";
import { getPlanStateLabel, planStateIds } from "../../models/planStateIds";
import { getPlanWorkflowActionsForState } from "../../models/planWorkflow";
import {
  canEditPlanRowsAtState,
  hasPlanRole,
  isPlanAdmin,
} from "../../models/planRoles";
import { confirmDialog } from "../../components/Dialog/DialogDelete";
import { commentDialog } from "../../components/Dialog/CommentDialog";
import { useNCALayerClient } from "../../functions/sign/SignHooks";
import { checkRoles } from "../../functions/checkRoles";
import PlanImportExcelModal from "./Modals/PlanImportExcelModal";
import PlanDocumentFormedTable from "./PlanDocumentFormedTable";
import { deserializePlanIndicatorsToPlanIndices } from "../../functions/planIndicatorsSerialization";
import { downloadPlanDocumentFormedXlsx } from "../../functions/planCodesReportXlsx";

const safeStr = (v) => (v == null ? "" : String(v));

const pickPlans = (doc) => {
  const list = doc?.plans || doc?.Plans || [];
  return Array.isArray(list) ? list : [];
};

/** Год плана: число 2000–2100. Бэк иногда отдаёт -1 / DateTime / ISO. */
const parsePlanYearNumber = (value) => {
  if (value == null || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    // Уже год (2026) или timestamp — timestamp отсекаем по диапазону
    if (value >= 2000 && value <= 2100) return Math.trunc(value);
    if (value > 1e11) {
      const y = new Date(value).getFullYear();
      return y >= 2000 && y <= 2100 ? y : null;
    }
    return null; // -1, 0 и т.п.
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    return y >= 2000 && y <= 2100 ? y : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}$/.test(trimmed)) {
      const y = Number(trimmed);
      return y >= 2000 && y <= 2100 ? y : null;
    }
    // ISO без timezone: считаем UTC (иначе UTC+5 превращает 01.01 в 31.12 прошлого года)
    const iso =
      /^\d{4}-\d{2}-\d{2}T/.test(trimmed) &&
      !/(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)
        ? `${trimmed}Z`
        : trimmed;
    const asDate = new Date(iso);
    if (!Number.isNaN(asDate.getTime())) {
      // Локальный год: 2025-12-31T19:00Z в UTC+5 = 01.01.2026
      const y = asDate.getFullYear();
      return y >= 2000 && y <= 2100 ? y : null;
    }
  }

  return null;
};

const yearToDate = (yearNum) => {
  const y = parsePlanYearNumber(yearNum) ?? new Date().getFullYear();
  return new Date(y, 0, 1);
};

/** Дата год+месяц из документа (month ISO / year). */
const periodFromDoc = (data) => {
  const raw = data?.month ?? data?.Month;
  if (raw != null && raw !== "") {
    let iso = raw;
    if (
      typeof raw === "string" &&
      /^\d{4}-\d{2}-\d{2}T/.test(raw) &&
      !/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)
    ) {
      iso = `${raw}Z`;
    }
    const asDate = new Date(iso);
    if (!Number.isNaN(asDate.getTime())) {
      return new Date(asDate.getFullYear(), asDate.getMonth(), 1);
    }
  }
  return yearToDate(data?.year ?? data?.Year);
};

/** 1-е число выбранного года/месяца в UTC — без сдвига дня (UTC+5 → 31.12). */
const yearToMonthIso = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(
      Date.UTC(value.getFullYear(), value.getMonth(), 1)
    ).toISOString();
  }
  const y = parsePlanYearNumber(value) ?? new Date().getFullYear();
  return new Date(Date.UTC(y, 0, 1)).toISOString();
};

export default function PlanDocumentEditForm() {
  const { id } = useParams();
  const history = useHistory();
  const isNew = id === "new";
  const alert = useAlert(false, 1);
  const { loading, start, stop } = useLoader(false);
  const { loading: workflowLoading, start: startWorkflow, stop: stopWorkflow } =
    useLoader(false);
  const { loading: saving, start: startSave, stop: stopSave } = useLoader(false);

  const [doc, setDoc] = useState(null);
  const [filialId, setFilialId] = useState(null);
  const [filialDivisionId, setFilialDivisionId] = useState(null);
  const [contractId, setContractId] = useState(null);
  const [districtCity, setDistrictCity] = useState("");
  const [year, setYear] = useState(new Date());
  const [comment, setComment] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [plans, setPlans] = useState([]);

  const [ncaClient] = useNCALayerClient();
  const ecpPendingRef = useRef(null);

  const stateId = Number(doc?.stateId ?? doc?.state?.id ?? (isNew ? 1 : NaN));
  const stateLabel =
    doc?.state?.name || getPlanStateLabel(stateId, isNew ? "Создан" : "—");
  const isSigned = Boolean(doc?.isSigned || doc?.IsSigned);
  const canEditRows = !isNew && canEditPlanRowsAtState(stateId);
  const canCreateDoc = isNew && (isPlanAdmin() || hasPlanRole("planning"));
  const showFormedPlan = useMemo(
    () =>
      [
        planStateIds.onSlaApproval,
        planStateIds.onApproval,
        planStateIds.approved,
      ].includes(stateId),
    [stateId]
  );

  const enrichPlansWithIndices = useCallback(async (list) => {
    const rows = Array.isArray(list) ? list : [];

    const mapPlanIndexRow = (r) => ({
      id: r.id,
      indexId: r.indexId ?? "",
      indexName: r.index?.name || r.indexName || "",
      serviceId: r.serviceId ?? r.service?.id ?? null,
      service: r.service || null,
      departmentId: r.departmentId ?? r.department?.id ?? null,
      departmentName: r.department?.name ?? null,
      groupId: r.groupId ?? r.group?.id ?? null,
      groupName: r.group?.name ?? null,
      plannedPlan: r.plannedPlan ?? 0,
      plannedResearch: r.plannedResearch ?? 0,
      plannedSum: r.plannedSum ?? 0,
      donePlan: r.donePlan ?? 0,
      doneResearch: r.doneResearch ?? 0,
      doneSum: r.doneSum ?? 0,
      _isDeleted: false,
    });

    const mergeIndices = (fromJson, fromApi) => {
      const map = new Map();
      const keyOf = (r) =>
        [
          r?.indexId ?? "",
          r?.serviceId ?? r?.service?.id ?? "",
          String(r?.indexName || r?.index?.name || "")
            .trim()
            .toLowerCase(),
          r?.plannedResearch ?? "",
          r?.plannedSum ?? "",
        ].join("|");
      [...(fromJson || []), ...(fromApi || [])].forEach((r) => {
        if (!r || r._isDeleted) return;
        const k = keyOf(r);
        if (!map.has(k)) map.set(k, r);
        else {
          // дополняем serviceId/имя, если в одной копии пусто
          const prev = map.get(k);
          map.set(k, {
            ...prev,
            ...r,
            serviceId: prev.serviceId || r.serviceId,
            service: prev.service || r.service,
            indexName: prev.indexName || r.indexName,
            plannedSum:
              Number(prev.plannedSum) > 0 ? prev.plannedSum : r.plannedSum,
          });
        }
      });
      return Array.from(map.values());
    };

    return Promise.all(
      rows.map(async (row) => {
        const planId = row?.id ?? row?.Id;
        if (planId == null) return row;
        try {
          let detail = row;
          try {
            detail = { ...row, ...(await planCRUD.get(planId)) };
          } catch {
            detail = row;
          }

          const fromJson = deserializePlanIndicatorsToPlanIndices(
            detail?.indicators ?? detail?.Indicators
          );

          let fromApi = [];
          try {
            const indicesResp = await planIndexCRUD.search({
              paging: { skip: 0, take: null, returnCount: true },
              filter: {
                planId: { operator: "equals", operand1: String(planId) },
              },
              sort: { id: { operator: "asc", ordinal: 0 } },
            });
            fromApi = (indicesResp?.result || []).map(mapPlanIndexRow);
          } catch {
            fromApi = [];
          }

          // Подтягиваем serviceId из planService, если в индексе пусто
          let services = [];
          try {
            const svcResp = await planServiceCRUD.search({
              paging: { skip: 0, take: null, returnCount: true },
              filter: {
                planId: { operator: "equals", operand1: String(planId) },
              },
              includes: ["Service"],
            });
            services = svcResp?.result || [];
          } catch {
            services = [];
          }

          let indices = mergeIndices(fromJson, fromApi);
          if (services.length) {
            indices = indices.map((idx) => {
              // Если serviceId уже есть — подтягиваем полный service (с code) из planService
              if (idx.serviceId) {
                const matched = services.find(
                  (s) =>
                    Number(s.serviceId ?? s.service?.id) === Number(idx.serviceId)
                );
                const svc = matched?.service || idx.service || null;
                return {
                  ...idx,
                  service: svc,
                  serviceCode: svc?.code || idx.serviceCode || null,
                };
              }
              const byIndex = services.find(
                (s) =>
                  Number(s.indexId ?? s.IndexId) === Number(idx.indexId) &&
                  (s.serviceId || s.service?.id)
              );
              const any = byIndex || services[0];
              const sid = any?.serviceId ?? any?.service?.id ?? null;
              if (!sid) return idx;
              return {
                ...idx,
                serviceId: sid,
                service: any?.service || idx.service || null,
                serviceCode: any?.service?.code || idx.serviceCode || null,
              };
            });

            // Если индексов нет, но есть planServices — покажем хотя бы их
            if (!indices.length) {
              indices = services.map((s, i) => ({
                id: s.id ?? `svc_${i}`,
                indexId: s.indexId ?? "",
                indexName: s.indexName || s.service?.name || "Исследование",
                serviceId: s.serviceId ?? s.service?.id ?? null,
                service: s.service || null,
                serviceCode: s.service?.code || null,
                departmentId: s.departmentId ?? null,
                departmentName: s.department?.name ?? null,
                groupId: null,
                groupName: null,
                plannedPlan: s.quantity ?? 0,
                plannedResearch: s.quantity ?? 0,
                plannedSum: s.price ?? 0,
                _isDeleted: false,
              }));
            }
          }

          // Догружаем service.code по serviceId, если вложенный service без кода
          const missingCodeSids = Array.from(
            new Set(
              indices
                .map((idx) => {
                  const sid = Number(idx?.serviceId ?? idx?.service?.id);
                  const hasCode = Boolean(
                    idx?.service?.code || idx?.serviceCode || idx?.researchCode
                  );
                  return Number.isFinite(sid) && sid > 0 && !hasCode ? sid : null;
                })
                .filter(Boolean)
            )
          );
          if (missingCodeSids.length) {
            const loaded = await Promise.all(
              missingCodeSids.map(async (sid) => {
                try {
                  const s = await serviceCRUD.get(sid);
                  return [sid, s];
                } catch {
                  return [sid, null];
                }
              })
            );
            const byId = new Map(loaded);
            indices = indices.map((idx) => {
              const sid = Number(idx?.serviceId ?? idx?.service?.id);
              if (!Number.isFinite(sid) || sid <= 0) return idx;
              if (idx?.service?.code || idx?.serviceCode) return idx;
              const s = byId.get(sid);
              if (!s) return idx;
              return {
                ...idx,
                service: s,
                serviceCode: s?.code || null,
              };
            });
          }

          return { ...detail, indices };
        } catch {
          return row;
        }
      })
    );
  }, []);

  const fetchDoc = useCallback(async () => {
    if (isNew || !id) return;
    const data = await planDocumentCRUD.get(id);
    setDoc(data || null);
    setFilialId(data?.filialId ?? data?.filial?.id ?? null);
    setFilialDivisionId(
      data?.filialDivisionId ?? data?.filialDivision?.id ?? null
    );
    setContractId(data?.contractId ?? data?.contract?.id ?? null);
    setDistrictCity(data?.districtCity ?? "");
    setYear(periodFromDoc(data));
    let list = pickPlans(data);
    if (!list.length) {
      const resp = await planCRUD.search({
        paging: { skip: 0, take: null, returnCount: true },
        filter: {
          planDocumentId: {
            operator: "equals",
            operand1: String(id),
          },
        },
        sort: { id: { operator: "asc", ordinal: 0 } },
      });
      list = resp?.result || [];
    }
    const docStateId = Number(
      data?.stateId ?? data?.state?.id ?? NaN
    );
    const needFormed = [
      planStateIds.onSlaApproval,
      planStateIds.onApproval,
      planStateIds.approved,
    ].includes(docStateId);
    setPlans(needFormed ? await enrichPlansWithIndices(list) : list);
  }, [enrichPlansWithIndices, id, isNew]);

  useEffect(() => {
    (async () => {
      if (isNew) return;
      try {
        start();
        await fetchDoc();
      } catch (e) {
        alert.show(0, e);
      } finally {
        stop();
      }
    })();
  }, [alert, fetchDoc, isNew, start, stop]);

  const workflowActions = useMemo(() => {
    if (isNew) return [];
    const actions = getPlanWorkflowActionsForState(stateId);
    const isAdmin = isPlanAdmin();
    return actions.map((action) => {
      if (isAdmin) return { ...action, allowed: true };
      const allowedRoles = action?.roles?.anyOf || [];
      const allowed = allowedRoles.length
        ? checkRoles(...allowedRoles)
        : true;
      return { ...action, allowed };
    });
  }, [isNew, stateId]);

  const createDocument = useCallback(async () => {
    if (!canCreateDoc) {
      alert.show(0, "Недостаточно прав для создания плана");
      return;
    }
    try {
      startSave();
      const employee = (() => {
        try {
          return JSON.parse(localStorage.employee || "null");
        } catch {
          return null;
        }
      })();
      const payload = {
        filialId: filialId || null,
        filialDivisionId: filialDivisionId || null,
        contractId: contractId || null,
        districtCity: districtCity || null,
        year: parsePlanYearNumber(year) ?? new Date().getFullYear(),
        month: yearToMonthIso(year),
        createdById: employee?.id || null,
      };
      const created = await planDocumentCRUD.create(payload);
      const newId = created?.id ?? created?.Id;
      if (!newId) throw new Error("Не удалось создать документ плана");
      alert.show(1, "Документ плана создан");
      history.replace(PLAN_DOCUMENT_ROUTE(newId));
    } catch (e) {
      alert.show(0, e);
    } finally {
      stopSave();
    }
  }, [
    alert,
    canCreateDoc,
    contractId,
    districtCity,
    filialDivisionId,
    filialId,
    history,
    startSave,
    stopSave,
    year,
  ]);

  const saveHeader = useCallback(async () => {
    if (isNew || !id) return;
    if (!canEditRows && !isPlanAdmin()) {
      alert.show(0, "Редактирование недоступно на текущем статусе");
      return;
    }
    try {
      startSave();
      await planDocumentCRUD.edit({
        ...doc,
        id: Number(id),
        stateId,
        filialId: filialId || null,
        filialDivisionId: filialDivisionId || null,
        contractId: contractId || null,
        districtCity: districtCity || null,
        year: parsePlanYearNumber(year) ?? new Date().getFullYear(),
        month: yearToMonthIso(year),
      });
      await fetchDoc();
      alert.show(1, "Сохранено");
    } catch (e) {
      alert.show(0, e);
    } finally {
      stopSave();
    }
  }, [
    alert,
    canEditRows,
    contractId,
    districtCity,
    doc,
    fetchDoc,
    filialDivisionId,
    filialId,
    id,
    isNew,
    startSave,
    stateId,
    stopSave,
    year,
  ]);

  const deletePlanRow = useCallback(
    async (planId) => {
      confirmDialog(
        "Удаление объекта",
        "Удалить строку объекта из плана?",
        async () => {
          try {
            start();
            const indices = await planIndexCRUD.search({
              paging: { skip: 0, take: null, returnCount: true },
              filter: {
                planId: { operator: "equals", operand1: String(planId) },
              },
            });
            for (const row of indices?.result || []) {
              if (row?.id != null) await planIndexCRUD.delete(row.id);
            }
            const services = await planServiceCRUD.search({
              paging: { skip: 0, take: null, returnCount: true },
              filter: {
                planId: { operator: "equals", operand1: String(planId) },
              },
            });
            for (const row of services?.result || []) {
              if (row?.id != null) await planServiceCRUD.delete(row.id);
            }
            await planCRUD.delete(planId);
            await fetchDoc();
            alert.show(1, "Объект удалён");
          } catch (e) {
            alert.show(0, e);
          } finally {
            stop();
          }
        }
      );
    },
    [alert, fetchDoc, start, stop]
  );

  const ensurePlanServicesForDocument = useCallback(async () => {
    for (const plan of plans) {
      const planId = plan?.id ?? plan?.Id;
      if (!planId) continue;
      const existing = await planServiceCRUD.search({
        paging: { skip: 0, take: 1, returnCount: false },
        filter: {
          planId: { operator: "equals", operand1: String(planId) },
        },
      });
      if ((existing?.result || []).length > 0) continue;
      // Коды выбираются в карточке строки; без них переход всё равно возможен,
      // но предупреждаем пользователя один раз на уровне UI ниже.
    }
    return true;
  }, [plans]);

  useEffect(() => {
    if (!ncaClient) return undefined;
    const prev = ncaClient.onmessage;
    ncaClient.onmessage = (msg) => {
      const pending = ecpPendingRef.current;
      if (pending) {
        ecpPendingRef.current = null;
        const ok = msg?.code === "200" || msg?.code === 200;
        if (ok && msg?.responseObject != null) {
          pending.resolve(msg.responseObject);
        } else {
          pending.reject(
            new Error(
              msg?.message ||
                (ok ? "Пустой ответ ЭЦП" : "Ошибка подписи через NCALayer")
            )
          );
        }
        return;
      }
      if (typeof prev === "function") prev(msg);
    };
    return () => {
      ncaClient.onmessage = prev;
    };
  }, [ncaClient]);

  const buildSignXml = useCallback(() => {
    const payload = {
      PlanDocumentWorkflowApproval: {
        _attributes: {
          planDocumentId: safeStr(id),
          fromStateId: String(planStateIds.onApproval),
          toStateId: String(planStateIds.approved),
          atUtc: new Date().toISOString(),
        },
      },
    };
    return js2xml(payload, { compact: true });
  }, [id]);

  const signXmlWithNca = useCallback(
    (xml) =>
      new Promise((resolve, reject) => {
        if (!ncaClient?.signXml) {
          reject(
            new Error(
              "NCALayer не готов. Установите NCALayer и обновите страницу."
            )
          );
          return;
        }
        if (ecpPendingRef.current) {
          reject(new Error("Подпись уже выполняется, подождите."));
          return;
        }
        ecpPendingRef.current = { resolve, reject };
        try {
          ncaClient.signXml("PKCS12", "SIGNATURE", xml);
        } catch (e) {
          ecpPendingRef.current = null;
          reject(e);
        }
      }),
    [ncaClient]
  );

  const runWorkflowTransition = useCallback(
    async ({ oldStateId, newStateId, commentText = null }) => {
      try {
        startWorkflow();
        await planDocumentCRUD.workflowRun(
          Number(id),
          Number(oldStateId),
          Number(newStateId),
          commentText == null ? "" : String(commentText)
        );
        await fetchDoc();
        alert.show(1, "Статус изменён");
      } catch (e) {
        alert.show(0, e);
      } finally {
        stopWorkflow();
      }
    },
    [alert, fetchDoc, id, startWorkflow, stopWorkflow]
  );

  const runWorkflowSigned = useCallback(
    async ({ commentText = null } = {}) => {
      try {
        startWorkflow();
        const xml = buildSignXml();
        const signedXml = await signXmlWithNca(xml);
        await planDocumentCRUD.workflowSignRunProtocol({
          id: Number(id),
          comment: commentText == null ? safeStr(comment) : String(commentText),
          sign: { xml, signedXml },
        });
        await fetchDoc();
        alert.show(1, "План утверждён и подписан");
      } catch (e) {
        alert.show(0, e);
      } finally {
        stopWorkflow();
      }
    },
    [
      alert,
      buildSignXml,
      comment,
      fetchDoc,
      id,
      signXmlWithNca,
      startWorkflow,
      stopWorkflow,
    ]
  );

  const openPlan = (planId, review) => {
    history.push(PLAN_EDIT_ROUTE(planId), {
      review,
      planDocumentId: Number(id),
      documentStateId: stateId,
    });
  };

  const headerReadOnly = isNew
    ? false
    : !(canEditRows || isPlanAdmin()) ||
      [planStateIds.onSlaApproval, planStateIds.onApproval, planStateIds.approved].includes(
        stateId
      );

  return (
    <DashboardLayout>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            {isNew
              ? "Новый план (документ)"
              : `План №${safeStr(id)} — ${stateLabel}`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={() => history.push(PLAN_LIST_ROUTE)}>
            К списку
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {!isNew && (
            <>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Статус" value={stateLabel} disabled />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Подписан"
                  value={isSigned ? "Да" : "Нет"}
                  disabled
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={3}>
            <ContractSelect
              label="Договор"
              year={
                year instanceof Date && !Number.isNaN(year.getTime())
                  ? year.getFullYear()
                  : new Date().getFullYear()
              }
              fullWidth
              value={contractId}
              disabled={headerReadOnly}
              onChange={(val) => setContractId(val)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FilialSelect
              label="Филиал"
              value={filialId}
              disabled={headerReadOnly}
              onChange={(v) => {
                setFilialId(v);
                setFilialDivisionId(null);
                setDistrictCity("");
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FilialDivisionSelect
              label="Районное отделение"
              value={filialDivisionId}
              filialId={filialId}
              disabled={headerReadOnly || !filialId}
              onChange={(v, obj) => {
                setFilialDivisionId(v);
                if (!districtCity && obj?.name) {
                  setDistrictCity(obj.name);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PlanDistrictCitySelect
              label="Город/район *"
              disabled={headerReadOnly || !filialId}
              fullWidth
              filialId={filialId || ""}
              value={districtCity || ""}
              onChange={(val) => setDistrictCity(val || "")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={ruLocale}>
              <DatePicker
                views={["year", "month"]}
                label="Год и месяц"
                value={year instanceof Date ? year : yearToDate(year)}
                disabled={headerReadOnly}
                onChange={(v) => {
                  if (v instanceof Date && !Number.isNaN(v.getTime())) {
                    setYear(new Date(v.getFullYear(), v.getMonth(), 1));
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          {!isNew && stateId === planStateIds.onApproval && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Комментарий к подписи"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isSigned}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {isNew ? (
                <LoadingButton
                  loading={saving}
                  variant="contained"
                  disabled={!canCreateDoc}
                  onClick={createDocument}
                >
                  Создать документ
                </LoadingButton>
              ) : (
                <>
                  {!headerReadOnly && (
                    <LoadingButton
                      loading={saving}
                      variant="outlined"
                      onClick={saveHeader}
                    >
                      Сохранить
                    </LoadingButton>
                  )}
                  {workflowActions.map((action) => (
                    <LoadingButton
                      key={action.key}
                      size="medium"
                      loading={workflowLoading}
                      variant="contained"
                      color={
                        action.key === "return_to_nce" ? "warning" : "primary"
                      }
                      disabled={!action.allowed || workflowLoading || loading}
                      title={
                        action.allowed
                          ? ""
                          : "Недостаточно прав для выполнения действия"
                      }
                      onClick={() => {
                        if (!action.allowed) return;
                        const [oldStateId, newStateId] = action.transition;
                        const { title, message } = action.confirm || {};

                        if (action.requiresEcpSign) {
                          const runSigned = (c) => {
                            void runWorkflowSigned({ commentText: c ?? null });
                          };
                          if (action.needsComment) {
                            commentDialog(
                              title || "Комментарий",
                              message || "Укажите комментарий",
                              ({ comment: c }) => runSigned(c)
                            );
                            return;
                          }
                          confirmDialog(
                            title || "Подтверждение",
                            message || "Выполнить действие?",
                            () => runSigned(null)
                          );
                          return;
                        }

                        const doRun = (c) =>
                          (async () => {
                            if (action.ensurePlanServices) {
                              await ensurePlanServicesForDocument();
                            }
                            await runWorkflowTransition({
                              oldStateId,
                              newStateId,
                              commentText: c ?? null,
                            });
                          })();

                        if (action.needsComment) {
                          commentDialog(
                            title || "Комментарий",
                            message || "Укажите комментарий",
                            ({ comment: c }) => doRun(c)
                          );
                          return;
                        }
                        confirmDialog(
                          title || "Подтверждение",
                          message || "Выполнить действие?",
                          () => doRun(null)
                        );
                      }}
                    >
                      {action.title}
                    </LoadingButton>
                  ))}
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {!isNew && showFormedPlan && (
        <Box sx={{ mb: 2 }}>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mb: 1 }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                downloadPlanDocumentFormedXlsx({
                  documentId: id,
                  stateName: stateLabel,
                  filialName: doc?.filial?.name || "",
                  plans,
                })
              }
            >
              Скачать сформированный план (Excel)
            </Button>
          </Stack>
          <PlanDocumentFormedTable
            plans={plans}
            title={
              stateId === planStateIds.approved
                ? "Утверждённый план"
                : stateId === planStateIds.onApproval
                  ? "План на утверждении"
                  : "План на согласовании СЛА"
            }
          />
        </Box>
      )}

      {!isNew && (
        <Paper sx={{ p: 2 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">
              {showFormedPlan ? "Объекты (кратко)" : "Объекты плана"}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {canEditRows && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setImportOpen(true)}
                >
                  Импорт Excel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    history.push(PLAN_DETAIL_ROUTE("new"), {
                      planDocumentId: Number(id),
                      documentStateId: stateId,
                    })
                  }
                >
                  Добавить объект
                </Button>
              </>
            )}
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Район/город</TableCell>
                <TableCell>Субъект</TableCell>
                <TableCell>ИИН/БИН</TableCell>
                <TableCell>Объект</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">
                      Нет объектов. Импортируйте Excel или добавьте вручную.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((row) => {
                  const planId = row.id ?? row.Id;
                  return (
                    <TableRow key={planId} hover>
                      <TableCell>{planId}</TableCell>
                      <TableCell>{row.districtCity || "—"}</TableCell>
                      <TableCell>{row.inspectedEntityName || "—"}</TableCell>
                      <TableCell>{row.individualOrBusinessId || "—"}</TableCell>
                      <TableCell>{row.inspectedObjectName || "—"}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          title="Просмотр"
                          onClick={() => openPlan(planId, true)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {canEditRows && (
                          <IconButton
                            size="small"
                            title="Редактировать"
                            onClick={() => openPlan(planId, false)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canEditRows && hasPlanRole("planning") && stateId === 1 && (
                          <IconButton
                            size="small"
                            title="Удалить"
                            color="error"
                            onClick={() => deletePlanRow(planId)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <PlanImportExcelModal
        open={importOpen}
        planDocumentId={Number(id)}
        onClose={() => setImportOpen(false)}
        onDone={() => {
          setImportOpen(false);
          fetchDoc();
        }}
      />
    </DashboardLayout>
  );
}
